// Quote Builder header button: shows how many likely duplicates this quote has
// and opens the compare + archive view.
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Copy, X } from "lucide-react";
import api from "../../../lib/api";
import DuplicateCompare from "./DuplicateCompare";

export default function DuplicatesButton({ quoteId, customerEmail, full = false }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    if (!quoteId) return;
    api.get(`/quote-duplicates/${quoteId}`).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [quoteId]);
  useEffect(() => { load(); }, [load, customerEmail]);
  const n = d?.duplicates?.length || 0;

  return (
    <>
      <button className={`t2-btn-ghost${full ? " t2-btn-full" : ""}`} onClick={() => { load(); setOpen(true); }} disabled={!quoteId} data-testid="btn-duplicates"
        title={n ? `${n} likely duplicate quote${n === 1 ? "" : "s"} for this customer` : "Check for duplicate quotes"}
        style={{ ...(full ? { fontSize: 10.5 } : {}), ...(n ? { borderColor: "rgba(245,158,11,0.55)", color: "#fcd34d" } : {}) }}>
        <Copy className="w-3.5 h-3.5" /> Duplicates{n ? ` (${n})` : ""}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="dupes-modal">
          <div className="w-full max-w-[1100px] max-h-[88vh] overflow-y-auto rounded-2xl border border-amber-500/30 bg-[#0F1420] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Copy className="w-5 h-5 text-amber-300" />
              <div className="text-[15px] font-semibold text-white">Duplicates of {d?.quote?.quote_ref}</div>
              <button type="button" onClick={() => setOpen(false)} className="ml-auto p-1.5 rounded hover:bg-white/10 text-slate-400" data-testid="dupes-modal-close" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {!d ? <div className="py-8 text-center text-slate-500 text-sm">Checking…</div> : n === 0 ? (
              <div className="py-8 text-center text-slate-400 text-sm" data-testid="dupes-modal-none">No duplicates found — same customer, same items &amp; total within 30 days.</div>
            ) : (
              <DuplicateCompare quotes={[d.quote, ...d.duplicates]} defaultKeepId={d.quote.id}
                onArchived={(ids) => { if (ids.includes(d.quote.id)) { setOpen(false); navigate("/quotes"); } else { load(); } }} />
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
